import * as React from "react";
import { BaseModal } from "../../../atoms/base-modal";
import classNames from "classnames";
import { useState } from "react";
import { matchType } from "../../../../application/domain/base/base-type";
import { RefundPayment } from "./refund-payment/refund-payment";
import { RefundRequestFactory } from "../../../../application/domain/payment/refund-request-factory";
import { RefundRequest } from "../../../../application/domain/payment/refund-request";
import { PaymentDetailByPaymentJSON } from "../../../../application/domain/payment/payment-summary";
import { CharCount } from "../../../molecules/char-count";
import { PaymentConst } from "@graffer-inc/cloud-gov-core";

type RefundStepLiterals = "INPUT" | "CONFIRM";

export const RefundModalV2: React.FC<{
  summary: PaymentDetailByPaymentJSON;
  isSubmitting: boolean;
  onClose: () => void;
  onOk: (request: RefundRequest, refundReasons: string) => void;
}> = ({ summary, isSubmitting, onClose, onOk }) => {
  const [step, setStep] = useState<RefundStepLiterals>("INPUT");
  const [refundType, setRefundType] = useState<"fullRefund" | "partialRefund">(
    "fullRefund",
  );
  const [refundRequest, setRefundRequest] = useState(
    RefundRequestFactory.createDefaultFromSummary(summary),
  );
  return (
    <BaseModal
      title={matchType(step, {
        INPUT: "返金する",
        CONFIRM: "返金内容を確認する",
      })}
      isActive={true}
      onClose={onClose}
    >
      {matchType(step, {
        INPUT: (
          <>
            <section className="modal-card-body">
              <div className="field">
                <label className="label">返金内容</label>
                <div className="buttons has-addons">
                  <button
                    className={classNames("button", {
                      "is-info": refundType === "fullRefund",
                      "is-selected": refundType === "fullRefund",
                    })}
                    onClick={() => setRefundType("fullRefund")}
                  >
                    全額返金
                  </button>
                  <button
                    className={classNames("button", {
                      "is-info": refundType === "partialRefund",
                      "is-selected": refundType === "partialRefund",
                    })}
                    onClick={() => setRefundType("partialRefund")}
                  >
                    一部返金
                  </button>
                </div>
              </div>
              <div className="field mt-5">
                {matchType(refundType, {
                  fullRefund: null,
                  partialRefund: (
                    <div>
                      <div className="notification">
                        費目ごとに返金額を入力してください。
                      </div>
                      <RefundPayment
                        refundRequest={refundRequest}
                        setRefundRequest={setRefundRequest}
                      />
                    </div>
                  ),
                })}
              </div>
              <div className="field mt-5">
                <label className="label">返金理由</label>
                <textarea
                  className="textarea"
                  placeholder="自動通知メールで申請者に返金内容が通知されます。"
                  value={refundRequest.getValue().message}
                  onChange={(e) => {
                    refundRequest.updateMessage(e.target.value);
                    setRefundRequest(refundRequest.copy());
                  }}
                />
                <div className="level">
                  <div className="level-left">
                    {!!refundRequest.messageError() && (
                      <p className="help is-danger">
                        {refundRequest.messageError()}
                      </p>
                    )}
                  </div>
                  <div className="level-right">
                    <CharCount
                      text={refundRequest.getValue().message}
                      maxLength={PaymentConst.MAX_PAYMENT_MESSAGE_LENGTH}
                    />
                  </div>
                </div>
              </div>
            </section>
            <section className="modal-card-foot">
              <button className="button is-fullwidth" onClick={onClose}>
                閉じる
              </button>
              <button
                className={classNames("button is-fullwidth is-info", {
                  "is-loading": isSubmitting,
                })}
                disabled={
                  !!refundRequest.messageError() ||
                  (refundType === "partialRefund" && !refundRequest.isValid())
                }
                onClick={() => {
                  setStep("CONFIRM");
                }}
              >
                返金内容の確認に進む
              </button>
            </section>
          </>
        ),
        CONFIRM: (
          <>
            <section className="modal-card-body">
              <div className="field">
                <label className="label">返金内容</label>
                {matchType(refundType, {
                  fullRefund: "全額返金",
                  partialRefund: "一部返金",
                })}
              </div>
              <div className="field mt-5">
                {matchType(refundType, {
                  fullRefund: null,
                  partialRefund: (
                    <div>
                      <RefundPayment
                        confirm={true}
                        refundRequest={refundRequest}
                        setRefundRequest={setRefundRequest}
                      />
                    </div>
                  ),
                })}
              </div>
              <div className="field mt-5">
                <label className="label">返金理由</label>
                <p>{refundRequest.getValue().message}</p>
              </div>
            </section>
            <section className="modal-card-foot">
              <button
                className="button is-fullwidth"
                onClick={() => {
                  setStep("INPUT");
                }}
              >
                戻る
              </button>
              <button
                className={classNames("button is-fullwidth is-info", {
                  "is-loading": isSubmitting,
                })}
                onClick={() => {
                  let req = refundRequest;
                  if (refundType === "fullRefund") {
                    // ignore input amount when fullRefund
                    req =
                      RefundRequestFactory.createFromPaymentSummary(summary);
                  }
                  onOk(req, refundRequest.getValue().message);
                }}
              >
                この内容で返金する
              </button>
            </section>
          </>
        ),
      })}
    </BaseModal>
  );
};
